/**
 * データ分析と可視化のための統計ユーティリティ関数
 */

import { simpleLinearRegression } from './linearRegression';

/**
 * 基底データポイント型（Grammar of Graphics用）
 * 型パラメータXでx軸の型を制約
 */
export interface DataPoint<X = number | string> {
  // 位置
  x: X;  // 型パラメータで柔軟に対応
  y: number;
  index: number;

  // Aesthetic channels
  color?: string | number;      // 色（カテゴリカル）
  fill?: string | number;       // 塗りつぶし色（カテゴリカル or 連続）
  stroke?: string | number;     // 枠線色（カテゴリカル）
  size?: number;                // サイズ（数値）
  shape?: string | number;      // 形状（カテゴリカル）
  alpha?: number;               // 透明度（数値、0-1）
  linetype?: string | number;   // 線種（カテゴリカル）
  group?: string | number;      // グループ化専用（視覚的変化なし）

  // Statistics変換用
  originalIndices?: number[];   // 集約/ビニング前の元の行インデックスリスト

  // Position adjustment用（Stack position）
  ymin?: number;                // スタック位置の開始値
  ymax?: number;                // スタック位置の終了値

  // 後方互換性のためのエイリアス
  /** @deprecated Use `color` instead */
  category?: string | number;
}

/**
 * 生データポイント（統計変換前）
 * originalIndicesを持たない
 */
export type RawDataPoint<X = number | string> = DataPoint<X>;

/**
 * 集約データポイント（統計変換後）
 * originalIndicesが必須
 */
export interface AggregatedDataPoint<X = number | string> extends DataPoint<X> {
  originalIndices: number[];
}

// =============================================================================
// 統計変換関数の型シグネチャ
// =============================================================================

/**
 * Count統計: カテゴリカル・数値両対応
 * X軸の値ごとにグループ化してカウント
 */
export type CountTransform = <X>(points: RawDataPoint<X>[]) => AggregatedDataPoint<X>[];

/**
 * Bin統計: 数値のみ
 * X軸を等間隔のビンに分割してヒストグラム作成
 */
export type BinTransform = (points: RawDataPoint<number>[]) => AggregatedDataPoint<number>[];

/**
 * Smooth統計: 数値のみ
 * 回帰曲線やloess平滑化
 */
export type SmoothTransform = (points: RawDataPoint<number>[]) => AggregatedDataPoint<number>[];

/**
 * Identity統計: 変換なし
 * カテゴリカル・数値両対応
 */
export type IdentityTransform = <X>(points: RawDataPoint<X>[]) => RawDataPoint<X>[];

// =============================================================================
// Grammar of Graphics用の統計変換関数
// =============================================================================

/**
 * Count統計: X軸の値とカテゴリカルaestheticsごとにカウント
 *
 * 計算量: O(n) ただしnはデータポイント数
 *
 * @param points データポイントの配列
 * @returns カウント後のデータポイント（元の行インデックスを保持）
 */
export function applyCountTransform<X>(points: RawDataPoint<X>[]): AggregatedDataPoint<X>[] {
  const groups = new Map<string, {
    x: X,
    indices: number[],
    color?: string | number,
    fill?: string | number,
    stroke?: string | number,
    shape?: string | number,
    linetype?: string | number
  }>();

  // X軸の値 + カテゴリカルaesthetics（color, fill, stroke, shape, linetype）の
  // 組み合わせごとにグループ化して、元の行インデックスを収集
  points.forEach(p => {
    // 配列を使って順序を明示的に保証（デバッグ時に可読）
    const key = JSON.stringify([
      p.x,
      p.color ?? p.category,
      p.fill,
      p.stroke,
      p.shape,
      p.linetype
    ]);

    if (!groups.has(key)) {
      groups.set(key, {
        x: p.x,
        indices: [],
        color: p.color ?? p.category,
        fill: p.fill,
        stroke: p.stroke,
        shape: p.shape,
        linetype: p.linetype
      });
    }
    groups.get(key)!.indices.push(p.index);
  });

  return Array.from(groups.values()).map(({x, indices, color, fill, stroke, shape, linetype}, i) => ({
    x,
    y: indices.length,  // カウント数
    index: i,
    originalIndices: indices,  // 元の行インデックスリスト
    color,
    category: color,  // 後方互換性のためのエイリアス
    fill,
    stroke,
    shape,
    linetype
  }));
}

/**
 * Bin統計: X軸をビンに分割してカウント（ヒストグラム用）
 *
 * 計算量: O(n) ただしnはデータポイント数
 *
 * @param points データポイントの配列
 * @param bins ビン数（デフォルト: 30）
 * @param binwidth ビン幅（指定時はbinsを無視）
 * @param yScale Y軸のスケール（'count': 件数、'density': 密度、デフォルト: 'count'）
 * @returns ビニング後のデータポイント（元の行インデックスを保持）
 */
export function applyBinTransform(
  points: RawDataPoint<number>[],
  bins: number = 30,
  binwidth?: number,
  yScale: 'count' | 'density' = 'count'
): AggregatedDataPoint<number>[] {
  if (points.length === 0) return [];

  const xValues = points.map(p => p.x);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);

  // ビン幅を決定
  const actualBinWidth = binwidth || (xMax - xMin) / bins;

  // ビン + カテゴリカルaesthetics（color, fill, stroke, shape, linetype）の
  // 組み合わせごとにグループ化して、元の行インデックスを収集
  const binGroups = new Map<string, {
    x: number,
    indices: number[],
    color?: string | number,
    fill?: string | number,
    stroke?: string | number,
    shape?: string | number,
    linetype?: string | number
  }>();

  points.forEach(p => {
    // ビンインデックスを計算（最大値は最後のビンに含める）
    let binIndex = Math.floor((p.x - xMin) / actualBinWidth);
    // 最大値がちょうどxMaxの場合、最後のビンに含める（範囲外を防ぐ）
    if (binIndex >= bins) {
      binIndex = bins - 1;
    }
    const binCenter = xMin + (binIndex + 0.5) * actualBinWidth;

    // ビン中心 + aestheticsの組み合わせでキーを作成
    const key = JSON.stringify([
      binCenter,
      p.color ?? p.category,
      p.fill,
      p.stroke,
      p.shape,
      p.linetype
    ]);

    if (!binGroups.has(key)) {
      binGroups.set(key, {
        x: binCenter,
        indices: [],
        color: p.color ?? p.category,
        fill: p.fill,
        stroke: p.stroke,
        shape: p.shape,
        linetype: p.linetype
      });
    }
    binGroups.get(key)!.indices.push(p.index);
  });

  // Y値の計算（count or density）
  const totalCount = points.length;
  const results = Array.from(binGroups.values()).map(({x, indices, color, fill, stroke, shape, linetype}, i) => {
    let yValue = indices.length;

    // densityスケールの場合、密度に変換: count / (totalCount * binWidth)
    if (yScale === 'density') {
      yValue = indices.length / (totalCount * actualBinWidth);
    }

    return {
      x,
      y: yValue,
      index: i,
      originalIndices: indices,  // 元の行インデックスリスト
      color,
      category: color,  // 後方互換性のためのエイリアス
      fill,
      stroke,
      shape,
      linetype
    };
  });

  return results;
}

/**
 * Smooth統計: 線形回帰による平滑化（lm）
 *
 * 計算量: O(n) ただしnはデータポイント数
 *
 * @param points データポイントの配列
 * @returns 平滑化後のデータポイント（100点）
 */
export function applyLinearRegressionSmooth(points: RawDataPoint<number>[]): AggregatedDataPoint<number>[] {
  if (points.length < 2) {
    // 入力が少ない場合はそのまま返す（originalIndicesを追加）
    const allIndices = points.map(p => p.index);
    return points.map(p => ({ ...p, originalIndices: allIndices }));
  }

  const x = points.map(p => p.x);
  const y = points.map(p => p.y);

  // 既存のlinearRegression関数を使用
  const result = simpleLinearRegression(x, y);
  const slope = result.coefficients[1];
  const intercept = result.coefficients[0];

  // X軸の範囲で等間隔に点を生成
  const xValues = [...x].sort((a, b) => a - b);
  const xMin = xValues[0];
  const xMax = xValues[xValues.length - 1];
  const step = (xMax - xMin) / 100;

  // 全ての元データのインデックス（平滑化曲線は全データから計算されるため）
  const allIndices = points.map(p => p.index);

  const smoothedPoints: AggregatedDataPoint<number>[] = [];
  for (let i = 0; i <= 100; i++) {
    const xi = xMin + i * step;
    const yi = slope * xi + intercept;
    smoothedPoints.push({ x: xi, y: yi, index: i, originalIndices: allIndices });
  }

  return smoothedPoints;
}

/**
 * Smooth統計: LOESS平滑化（局所重み付き回帰）
 *
 * 計算量: O(n^2) ただしnはデータポイント数
 *
 * @param points データポイントの配列
 * @param span 近傍点の割合（0.1-1.0、デフォルト: 0.75）
 * @returns 平滑化後のデータポイント
 */
export function applyLoessSmooth(points: RawDataPoint<number>[], span: number = 0.75): AggregatedDataPoint<number>[] {
  if (points.length < 2) {
    // 入力が少ない場合はそのまま返す（originalIndicesを追加）
    const allIndices = points.map(p => p.index);
    return points.map(p => ({ ...p, originalIndices: allIndices }));
  }

  const sortedPoints = [...points].sort((a, b) => a.x - b.x);
  const n = sortedPoints.length;
  const bandwidth = Math.max(2, Math.floor(span * n));

  // 全ての元データのインデックス（平滑化曲線は全データから計算されるため）
  const allIndices = points.map(p => p.index);

  const smoothedPoints: AggregatedDataPoint<number>[] = [];

  for (let i = 0; i < n; i++) {
    const x0 = sortedPoints[i].x;

    // 近傍点を取得
    const neighbors: Array<{ x: number; y: number; distance: number }> = sortedPoints.map(p => ({
      x: p.x,
      y: p.y,
      distance: Math.abs(p.x - x0),
    })).sort((a, b) => a.distance - b.distance).slice(0, bandwidth);

    const maxDist = neighbors[neighbors.length - 1].distance;
    if (maxDist === 0) {
      smoothedPoints.push({ x: x0, y: sortedPoints[i].y, index: i, originalIndices: allIndices });
      continue;
    }

    // 重み付き線形回帰
    let sumW = 0;
    let sumWX = 0;
    let sumWY = 0;
    let sumWX2 = 0;
    let sumWXY = 0;

    neighbors.forEach(({ x, y, distance }) => {
      const u = distance / maxDist;
      const w = (1 - u * u * u) ** 3; // Tricube weight
      sumW += w;
      sumWX += w * x;
      sumWY += w * y;
      sumWX2 += w * x * x;
      sumWXY += w * x * y;
    });

    const slope = (sumW * sumWXY - sumWX * sumWY) / (sumW * sumWX2 - sumWX * sumWX);
    const intercept = (sumWY - slope * sumWX) / sumW;
    const y0 = slope * x0 + intercept;

    smoothedPoints.push({ x: x0, y: y0, index: i, originalIndices: allIndices });
  }

  return smoothedPoints;
}

// =============================================================================
// Grammar of Graphics用の位置調整関数
// =============================================================================

/**
 * Jitter位置調整: ランダムオフセットを追加
 *
 * 計算量: O(n) ただしnはデータポイント数
 *
 * @param points データポイントの配列（数値X座標のみ）
 * @param widthFactor X方向のジッター幅（0-1、デフォルト: 0.4）
 * @param heightFactor Y方向のジッター幅（0-1、デフォルト: 0.4）
 * @returns ジッター後のデータポイント
 */
export function applyJitterPosition(
  points: DataPoint<number>[],
  widthFactor: number = 0.4,
  heightFactor: number = 0.4
): DataPoint<number>[] {
  if (points.length === 0) return [];

  // データの範囲を計算
  const xValues = points.map(p => p.x);
  const yValues = points.map(p => p.y);
  const xRange = Math.max(...xValues) - Math.min(...xValues);
  const yRange = Math.max(...yValues) - Math.min(...yValues);

  // ジッター幅を計算（データ範囲に対する割合）
  const xJitter = xRange * widthFactor * 0.1; // 10%のスケーリング
  const yJitter = yRange * heightFactor * 0.1;

  // 各ポイントにランダムオフセットを追加
  return points.map(p => ({
    ...p,
    x: p.x + (Math.random() - 0.5) * xJitter,
    y: p.y + (Math.random() - 0.5) * yJitter,
  }));
}

/**
 * Dodge位置調整: カテゴリごとに横に並べる
 *
 * 計算量: O(n) ただしnはデータポイント数
 *
 * @param points データポイントの配列（数値X座標のみ）
 * @param width 並列配置の幅（0-1、デフォルト: 0.9）
 * @returns Dodge後のデータポイント
 */
export function applyDodgePosition(
  points: DataPoint<number>[],
  width: number = 0.9
): DataPoint<number>[] {
  if (points.length === 0) return [];

  // カテゴリごとにグループ化（fillまたはcolorまたはcategoryを使用）
  const grouped = new Map<number, Array<{ point: DataPoint<number>; categoryIndex: number }>>();
  const categories = new Set(points.map(p => p.fill ?? p.color ?? p.category).filter(c => c !== undefined));
  const categoryArray = Array.from(categories);

  points.forEach(p => {
    const x = p.x;
    if (!grouped.has(x)) {
      grouped.set(x, []);
    }
    const category = p.fill ?? p.color ?? p.category;
    const categoryIndex = category !== undefined ? categoryArray.indexOf(category) : 0;
    grouped.get(x)!.push({ point: p, categoryIndex });
  });

  // X軸の最小間隔を計算（dodge幅の基準）
  const xValues = Array.from(grouped.keys()).sort((a, b) => a - b);
  let minGap = Infinity;
  for (let i = 1; i < xValues.length; i++) {
    const gap = xValues[i] - xValues[i - 1];
    if (gap > 0) minGap = Math.min(minGap, gap);
  }
  if (!isFinite(minGap)) minGap = 1;

  const dodgeWidth = minGap * width;
  const categoryCount = Math.max(categoryArray.length, 1);

  // 各グループ内でカテゴリごとにオフセット
  const result: DataPoint<number>[] = [];
  grouped.forEach((group, x) => {
    group.forEach(({ point, categoryIndex }) => {
      const offset = (categoryIndex - (categoryCount - 1) / 2) * (dodgeWidth / categoryCount);
      result.push({
        ...point,
        x: x + offset,
      });
    });
  });

  return result;
}

/**
 * Stack位置調整: カテゴリごとに縦に積み上げる
 *
 * 計算量: O(n log n) ただしnはデータポイント数
 *
 * @param points データポイントの配列（数値X座標のみ）
 * @returns Stack後のデータポイント
 */
export function applyStackPosition(points: DataPoint<number>[]): DataPoint<number>[] {
  if (points.length === 0) return [];

  // X座標とカテゴリでグループ化（fillまたはcolorまたはcategoryを使用）
  const grouped = new Map<number, Map<string | number, DataPoint<number>[]>>();

  points.forEach(p => {
    const x = p.x;
    const category = p.fill ?? p.color ?? p.category ?? 'default';

    if (!grouped.has(x)) {
      grouped.set(x, new Map());
    }
    const categoryMap = grouped.get(x)!;
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push(p);
  });

  // カテゴリの順序を決定（すべてのX座標で一貫性を保つ）
  const allCategories = new Set<string | number>();
  grouped.forEach(categoryMap => {
    categoryMap.forEach((_, category) => allCategories.add(category));
  });
  const categoryOrder = Array.from(allCategories);

  // 各X座標で積み上げ
  const result: DataPoint<number>[] = [];
  grouped.forEach((categoryMap, _x) => {
    let cumulativeY = 0;

    categoryOrder.forEach(category => {
      const categoryPoints = categoryMap.get(category) || [];
      categoryPoints.forEach(p => {
        const ymin = cumulativeY;
        const ymax = cumulativeY + p.y;
        result.push({
          ...p,
          y: ymax,
          ymin,
          ymax,
        });
        cumulativeY = ymax;
      });
    });
  });

  return result;
}

/**
 * Identity位置調整: そのままの位置に配置（重なりを許容）
 *
 * ggplot2の position_identity() に相当
 * グループ化されたヒストグラムを重ねて表示する際に使用
 * 透明度（alpha）を設定しないと重なりが見えない
 *
 * @param points データポイントの配列
 * @returns そのまま返す（変換なし）
 */
export function applyIdentityPosition(points: DataPoint<number>[]): DataPoint<number>[] {
  // Identity positionは位置調整を行わない
  // ポイントをそのまま返す
  return points;
}

/**
 * Fill位置調整: 積み上げた後、各X座標で100%に正規化
 *
 * ggplot2の position_fill() に相当
 * 各ビンの相対的な割合を比較するのに便利
 *
 * 計算量: O(n log n) ただしnはデータポイント数
 *
 * @param points データポイントの配列（数値X座標のみ）
 * @returns Fill後のデータポイント（Y座標が0-1の範囲に正規化される）
 */
export function applyFillPosition(points: DataPoint<number>[]): DataPoint<number>[] {
  if (points.length === 0) return [];

  // まずStackと同じようにグループ化（fillまたはcolorまたはcategoryを使用）
  const grouped = new Map<number, Map<string | number, DataPoint<number>[]>>();

  points.forEach(p => {
    const x = p.x;
    const category = p.fill ?? p.color ?? p.category ?? 'default';

    if (!grouped.has(x)) {
      grouped.set(x, new Map());
    }
    const categoryMap = grouped.get(x)!;
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push(p);
  });

  // カテゴリの順序を決定
  const allCategories = new Set<string | number>();
  grouped.forEach(categoryMap => {
    categoryMap.forEach((_, category) => allCategories.add(category));
  });
  const categoryOrder = Array.from(allCategories);

  // 各X座標で積み上げ、その後正規化
  const result: DataPoint<number>[] = [];
  grouped.forEach((categoryMap, _x) => {
    // まず合計を計算
    let total = 0;
    categoryOrder.forEach(category => {
      const categoryPoints = categoryMap.get(category) || [];
      categoryPoints.forEach(p => {
        total += p.y;
      });
    });

    // 合計が0の場合は何もしない
    if (total === 0) {
      return;
    }

    // 積み上げながら正規化
    let cumulativeY = 0;
    categoryOrder.forEach(category => {
      const categoryPoints = categoryMap.get(category) || [];
      categoryPoints.forEach(p => {
        const normalizedY = p.y / total; // 0-1の範囲に正規化
        const ymin = cumulativeY;
        const ymax = cumulativeY + normalizedY;
        result.push({
          ...p,
          y: ymax,
          ymin,
          ymax,
        });
        cumulativeY = ymax;
      });
    });
  });

  return result;
}

// =============================================================================
// カーネル密度推定用のユーティリティ関数
// =============================================================================

/**
 * Silvermanの経験則を用いてカーネル密度推定のバンド幅を計算
 *
 * 計算式: 0.9 * min(std, IQR/1.34) * n^(-0.2)
 * stdは標準偏差、IQRは四分位範囲
 *
 * 計算量: O(n log n) ただしnはデータ数
 * - ソート（四分位数計算用）: O(n log n)
 * - 平均・標準偏差: O(n)
 *
 * @param data - 数値の配列
 * @returns カーネル密度推定用のバンド幅
 */
export function calculateBandwidth(data: number[]): number {
  if (data.length === 0) return 1; // デフォルト値

  const n = data.length;
  const sorted = [...data].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(n * 0.25)];
  const q3 = sorted[Math.floor(n * 0.75)];
  const iqr = q3 - q1;

  // 平均を一度だけ計算（O(n)）
  const mean = data.reduce((a, b) => a + b, 0) / n;
  const std = Math.sqrt(data.reduce((sum, x) => {
    const diff = x - mean;
    return sum + diff * diff;
  }, 0) / n);

  // Silverman's rule of thumb
  return 0.9 * Math.min(std, iqr / 1.34) * Math.pow(n, -0.2);
}

/**
 * Density統計: カーネル密度推定（KDE）
 * X軸の連続分布を推定する
 *
 * 計算量: O(n * m) ただしnはデータポイント数、mは出力点数（100点）
 *
 * @param points - データポイントの配列
 * @param bandwidth - カーネル帯域幅（省略時は自動計算）
 * @param yScale - Y軸スケール（'density': 確率密度、'count': カウント数相当）
 * @returns 密度曲線のデータポイント（100点）
 */
export function applyDensityTransform(
  points: RawDataPoint<number>[],
  bandwidth?: number,
  yScale: 'density' | 'count' = 'density'
): AggregatedDataPoint<number>[] {
  if (points.length === 0) return [];

  // カテゴリカルaesthetics（color, fill, stroke, shape, linetype）ごとにグループ化
  const groups = new Map<string, RawDataPoint<number>[]>();

  points.forEach(p => {
    const key = JSON.stringify([
      p.color ?? p.category,
      p.fill,
      p.stroke,
      p.shape,
      p.linetype
    ]);

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(p);
  });

  // 各グループごとに密度推定を実行
  const results: AggregatedDataPoint<number>[] = [];
  let index = 0;

  groups.forEach((groupPoints) => {
    // グループ内の全データのxValues
    const xValues = groupPoints.map(p => p.x);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const allIndices = groupPoints.map(p => p.index);

    // バンド幅を決定（手動指定 or 自動計算）
    const bw = bandwidth || calculateBandwidth(xValues);

    // ガウシアンカーネル
    const kernel = (u: number): number => {
      return Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);
    };

    // 密度推定関数
    const estimateDensity = (x: number): number => {
      const n = xValues.length;
      let sum = 0;
      for (let i = 0; i < n; i++) {
        sum += kernel((x - xValues[i]) / bw);
      }
      return sum / (n * bw);
    };

    // 100点で密度曲線を生成
    const numPoints = 100;
    const step = (xMax - xMin) / (numPoints - 1);

    // グループのaesthetics（最初のポイントから取得）
    const firstPoint = groupPoints[0];

    for (let i = 0; i < numPoints; i++) {
      const x = xMin + i * step;
      const y = estimateDensity(x);

      results.push({
        x,
        y,
        index: index++,
        originalIndices: allIndices,
        color: firstPoint.color ?? firstPoint.category,
        category: firstPoint.color ?? firstPoint.category,
        fill: firstPoint.fill,
        stroke: firstPoint.stroke,
        shape: firstPoint.shape,
        linetype: firstPoint.linetype
      });
    }
  });

  // yScale = 'count' の場合、カウント数スケールに変換
  if (yScale === 'count') {
    // グループごとにスケーリング係数を計算
    const groupScales = new Map<string, number>();

    groups.forEach((groupPoints, key) => {
      const xValues = groupPoints.map(p => p.x);
      const xMin = Math.min(...xValues);
      const xMax = Math.max(...xValues);

      // ビン数をSturgesの公式で計算
      const bins = Math.ceil(Math.log2(groupPoints.length) + 1);
      const binWidth = (xMax - xMin) / bins;

      // スケーリング係数 = データ数 × ビン幅
      const scaleFactor = groupPoints.length * binWidth;
      groupScales.set(key, scaleFactor);
    });

    // 各結果ポイントにスケーリングを適用
    results.forEach(p => {
      const key = JSON.stringify([
        p.color ?? p.category,
        p.fill,
        p.stroke,
        p.shape,
        p.linetype
      ]);
      const scaleFactor = groupScales.get(key) || 1;
      p.y *= scaleFactor;
    });
  }

  return results;
}

// =============================================================================
// Summary統計変換
// =============================================================================

/**
 * Summary集約関数の型
 */
export type SummaryFunctionType = 'mean' | 'sum' | 'median' | 'min' | 'max' | 'count';

/**
 * Statisticsで使用可能なaestheticチャンネル
 */
export type StatAestheticChannel = 'x' | 'y' | 'color' | 'fill' | 'size' | 'alpha' | 'group';

/**
 * Summary統計のオプション
 */
export interface SummaryOptions {
  groupBy?: StatAestheticChannel[];  // グループ化キー（デフォルト: 自動推論）
  input?: StatAestheticChannel;      // 集約対象（デフォルト: 'y'）
  output?: StatAestheticChannel;     // 出力先（デフォルト: inputと同じ）
  complete?: boolean;                // X×Yの全組み合わせを生成（欠損は0で補完）
  // Issue#241: Facet使用時に全パネルで共通のX/Y値を使用して補完
  sharedXValues?: unknown[];         // 全パネル共通のX値セット
  sharedYValues?: unknown[];         // 全パネル共通のY値セット（2D補完用、省略時は1D補完）
}

/**
 * DataPointからaestheticの値を取得
 */
function getAestheticValue<X>(point: DataPoint<X>, channel: StatAestheticChannel): number | string | undefined {
  switch (channel) {
    case 'x': return point.x as number | string;
    case 'y': return point.y;
    case 'color': return point.color;
    case 'fill': return point.fill;
    case 'size': return point.size;
    case 'alpha': return point.alpha;
    case 'group': return point.group;
    default: return undefined;
  }
}

/**
 * DataPointにaestheticの値を設定
 */
function setAestheticValue<X>(point: DataPoint<X>, channel: StatAestheticChannel, value: number | undefined): DataPoint<X> {
  const result = { ...point };
  switch (channel) {
    case 'y': result.y = value ?? 0; break;
    case 'fill': result.fill = value; break;
    case 'color': result.color = value; break;
    case 'size': result.size = value; break;
    case 'alpha': result.alpha = value; break;
    // x, groupへの出力は通常ないが、型の完全性のため
  }
  return result;
}

/**
 * 数値の配列を集約関数で集約
 */
function aggregateValues(values: number[], fun: SummaryFunctionType): number | undefined {
  if (values.length === 0) return undefined;

  switch (fun) {
    case 'mean':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    }
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    case 'count':
      return values.length;
  }
}

/**
 * Summary統計: グループ化して集約
 *
 * GoGの原理に従い、groupBy/input/outputを指定可能。
 * - groupBy: グループ化キー（デフォルト: x + カテゴリカルaesthetics + group）
 * - input: 集約対象のaesthetic（デフォルト: 'y'）
 * - output: 出力先aesthetic（デフォルト: inputと同じ）
 *
 * @param points データポイントの配列
 * @param fun 集約関数
 * @param options groupBy, input, outputの指定
 */
export function applySummaryTransform<X>(
  points: RawDataPoint<X>[],
  fun: SummaryFunctionType = 'mean',
  options: SummaryOptions = {}
): AggregatedDataPoint<X>[] {
  if (points.length === 0) return [];

  // デフォルト値の解決
  const input = options.input ?? 'y';
  const output = options.output ?? input;
  // groupByのデフォルト: x + カテゴリカルaesthetics (color, stroke, shape, linetype, group)
  // inputがyでない場合はyもグループ化キーに含める
  const defaultGroupBy: StatAestheticChannel[] = ['x', 'color', 'group'];
  if (input !== 'y') {
    defaultGroupBy.push('y');
  }
  const groupBy = options.groupBy ?? defaultGroupBy;

  // グループ化
  const groups = new Map<string, {
    points: RawDataPoint<X>[],
    inputValues: number[],
    indices: number[]
  }>();

  points.forEach(p => {
    // グループ化キーを生成
    const keyParts = groupBy.map(channel => {
      const value = getAestheticValue(p, channel);
      return value ?? null;
    });
    const key = JSON.stringify(keyParts);

    if (!groups.has(key)) {
      groups.set(key, { points: [], inputValues: [], indices: [] });
    }

    const group = groups.get(key)!;
    group.points.push(p);
    group.indices.push(p.index);

    // inputの値を収集（数値のみ）
    const inputValue = getAestheticValue(p, input);
    if (typeof inputValue === 'number' && !isNaN(inputValue)) {
      group.inputValues.push(inputValue);
    }
  });

  // 各グループを集約
  const aggregatedPoints = Array.from(groups.values()).map(({ points: groupPoints, inputValues, indices }, i) => {
    const first = groupPoints[0];
    const aggregated = aggregateValues(inputValues, fun);

    // 基本のポイントを作成（最初のポイントをベースに）
    const result: AggregatedDataPoint<X> = {
      x: first.x,
      y: first.y,
      index: i,
      originalIndices: indices,
      color: first.color,
      fill: first.fill,
      stroke: first.stroke,
      shape: first.shape,
      linetype: first.linetype,
      group: first.group,
      category: first.color,  // 後方互換性
    };

    // 出力先に集約結果を設定
    return setAestheticValue(result, output, aggregated) as AggregatedDataPoint<X>;
  });

  // complete: true の場合、欠損X×Y組み合わせを0で補完
  if (options.complete) {
    return completeXYGrid(aggregatedPoints, output, {
      sharedXValues: options.sharedXValues,
      sharedYValues: options.sharedYValues,
    });
  }

  return aggregatedPoints;
}

/**
 * 補完オプション
 */
interface CompleteGridOptions {
  sharedXValues?: unknown[];  // 全パネル共通のX値セット
  sharedYValues?: unknown[];  // 全パネル共通のY値セット（省略時は1D補完）
}

/**
 * X×Yの全組み合わせを生成し、欠損は0で補完
 * Issue#241: Facet使用時に全パネルで共通のX/Y値を使用可能
 * sharedYValuesが省略された場合は1D（X only）補完を行う
 */
function completeXYGrid<X>(
  points: AggregatedDataPoint<X>[],
  output: StatAestheticChannel,
  options: CompleteGridOptions = {}
): AggregatedDataPoint<X>[] {
  if (points.length === 0) return points;

  // X値を収集：共有値があればそれを使用、なければポイントから収集
  const xValues = new Set<X>();
  if (options.sharedXValues && options.sharedXValues.length > 0) {
    options.sharedXValues.forEach(v => xValues.add(v as X));
  } else {
    points.forEach(p => xValues.add(p.x));
  }

  // Y値を収集：共有値があればそれを使用、なければポイントから収集
  // sharedYValuesが明示的に空配列の場合は1D補完モード
  const yValues = new Set<number>();
  const is1DMode = options.sharedYValues !== undefined && options.sharedYValues.length === 0;

  if (is1DMode) {
    // 1D補完モード: Y値はポイントごとに固定
    // 各Xについて、既存のY値がなければ補完
    points.forEach(p => yValues.add(p.y));
  } else if (options.sharedYValues && options.sharedYValues.length > 0) {
    options.sharedYValues.forEach(v => {
      const numVal = typeof v === 'number' ? v : Number(v);
      if (!isNaN(numVal)) {
        yValues.add(numVal);
      } else {
        // 文字列Y値の場合はそのまま追加（キー生成に使用）
        yValues.add(v as unknown as number);
      }
    });
  } else {
    points.forEach(p => yValues.add(p.y));
  }

  // 既存の組み合わせを収集
  const existingKeys = new Set<string>();
  points.forEach(p => {
    if (is1DMode) {
      // 1D: Xのみでキーを生成
      existingKeys.add(`${String(p.x)}`);
    } else {
      // 2D: X×Yでキーを生成
      existingKeys.add(`${String(p.x)}__${p.y}`);
    }
  });

  // 欠損組み合わせを追加
  const result = [...points];
  let nextIndex = points.length;

  if (is1DMode) {
    // 1D補完: Xのみを補完、Yはポイントから取得した最初の値を使用
    const firstY = points[0]?.y ?? 0;
    xValues.forEach(x => {
      const key = `${String(x)}`;
      if (!existingKeys.has(key)) {
        // 欠損ポイントを作成（outputに0を設定）
        const emptyPoint: AggregatedDataPoint<X> = {
          x,
          y: firstY,
          index: nextIndex++,
          originalIndices: [],  // 元データなし
        };
        result.push(setAestheticValue(emptyPoint, output, 0) as AggregatedDataPoint<X>);
      }
    });
  } else {
    // 2D補完: X×Yの全組み合わせ
    xValues.forEach(x => {
      yValues.forEach(y => {
        const key = `${String(x)}__${y}`;
        if (!existingKeys.has(key)) {
          // 欠損ポイントを作成（outputに0を設定）
          const emptyPoint: AggregatedDataPoint<X> = {
            x,
            y,
            index: nextIndex++,
            originalIndices: [],  // 元データなし
          };
          result.push(setAestheticValue(emptyPoint, output, 0) as AggregatedDataPoint<X>);
        }
      });
    });
  }

  return result;
}
